import { CitationOut, DocumentWithDownloadUrl as Document, SourceOut } from '@/client';
import { linkifyContactDetails } from './contactLinkify';
import { getProtectedRanges, isWithinProtectedRange, withProtectedMarkdown } from './markdownProtect';
import { findDocumentById } from '@/utils/documentUtils.ts';

/**
 * A canonical citation marker, e.g. "[1]". Defined once (no capture groups, so it composes into
 * larger patterns) and shared so the helpers can't drift apart. The optional comma-suffix is kept
 * tolerant for stray shapes that slip through, but the server emits bare `[n]`.
 */
const CITATION_TOKEN = '\\[\\d+(?:,[^\\]]*)?\\]';
const CITATION_TRAILING_PUNCTUATION_RE = new RegExp(
	`\\s*((?:${CITATION_TOKEN}\\s*)+)([.!?]|:(?=[\\t ]*\\n[\\t ]*(?:[-*+]|\\d+[.)])[\\t ]+))`,
	'g',
);

/**
 * Moves citation markers that sit before trailing punctuation to after it, so
 * "... well-ventilated [1]." reads "... well-ventilated.[1]". Handles a run of adjacent markers
 * and keeps their order. Colons are included for headings that introduce a list.
 */
export function moveCitationsAfterPunctuation(text: string): string {
	return text.replace(
		CITATION_TRAILING_PUNCTUATION_RE,
		(_full, citations: string, stop: string) => `${stop}${citations.trimEnd()}`,
	);
}

const VISIBLE_CITATION_RE = /\[\d+(?:,[^\]]*)?]/g;
const PARAGRAPH_SEPARATOR_RE = /(\n{2,})/g;
const BLANK_PARAGRAPH_SEPARATOR_RE = /^\n{2,}$/;
const LIST_ITEM_LINE_RE = /^\s*(?:[-*+]|\d+[.)])\s+/m;
const TRAILING_PUNCTUATION_RE = /[.!?)]$/;

const moveCitationsToSegmentEnd = (text: string, textStart: number, protectedRanges: [number, number][]): string => {
	const citations: string[] = [];
	const withoutCitations = text
		.replace(VISIBLE_CITATION_RE, (token: string, offset: number) => {
			if (isWithinProtectedRange(textStart + offset, protectedRanges)) return token;
			citations.push(token);
			return '';
		})
		.replace(/[ \t]+([.!?,;:])/g, '$1')
		.replace(/[ \t]{2,}/g, ' ')
		.trimEnd();

	if (citations.length === 0) return text;

	const separator = TRAILING_PUNCTUATION_RE.test(withoutCitations) ? '' : ' ';
	return `${withoutCitations}${separator}${citations.join(' ')}`;
};

const moveListItemCitationsToLineEnd = (
	text: string,
	textStart: number,
	protectedRanges: [number, number][],
): string => {
	let lineStart = textStart;
	return text
		.split(/(\n)/)
		.map((line) => {
			const currentLineStart = lineStart;
			lineStart += line.length;
			if (line === '\n' || !LIST_ITEM_LINE_RE.test(line)) return line;
			return moveCitationsToSegmentEnd(line, currentLineStart, protectedRanges);
		})
		.join('');
};

/**
 * Moves visible citation markers to the end of each paragraph, preserving reading order. This keeps
 * the backend's per-marker citation contract intact while avoiding sentence-by-sentence source
 * clutter in rendered prose. Protected markdown spans are hidden while this runs, so code and links
 * keep their original text.
 */
export function moveCitationsToParagraphEnd(text: string): string {
	const protectedRanges = getProtectedRanges(text);
	let partStart = 0;

	return text
		.split(PARAGRAPH_SEPARATOR_RE)
		.map((part) => {
			const currentPartStart = partStart;
			partStart += part.length;

			if (BLANK_PARAGRAPH_SEPARATOR_RE.test(part)) return part;
			if (LIST_ITEM_LINE_RE.test(part)) {
				return moveListItemCitationsToLineEnd(part, currentPartStart, protectedRanges);
			}

			return moveCitationsToSegmentEnd(part, currentPartStart, protectedRanges);
		})
		.join('');
}

/**
 * The href a citation marker is wrapped in, e.g. `#cite-0`. The trailing number is the marker's
 * reading-order index — used to look it up in the answer's parallel `citations[]`, so two `[1]`
 * markers pointing at the same document but different pages stay distinguishable.
 */
const CONTRACT_CITATION_HREF_PREFIX = '#cite-';
const CONTRACT_CITATION_HREF_INDEX_SEPARATOR = ',';

/** Reads the citation index back out of a `#cite-N` href, or null if it isn't one. */
export function getContractCitationIndex(href: string | undefined): number | null {
	return getContractCitationIndices(href)[0] ?? null;
}

/**
 * Wraps a citation marker in a *Markdown* link (raw HTML is disabled in the answer renderer, so an
 * `<a>` tag would render as literal text). The marker's own brackets are escaped so they stay
 * verbatim link text instead of being parsed as nested link syntax.
 */
const buildCitationLink = (label: string, href: string): string => `[${label.replace(/[[\]]/g, '\\$&')}](${href})`;

/** Regex source matching one injected citation link, capturing its href index. */
const citationLinkPattern = String.raw`\[\\\[\d+\\\]\]\(${CONTRACT_CITATION_HREF_PREFIX}(\d+)\)`;

/** Reads one or more citation indices back out of a grouped citation href, e.g. `#cite-0,1`. */
export function getContractCitationIndices(href: string | undefined): number[] {
	if (!href) return [];
	const match = new RegExp(
		`^${CONTRACT_CITATION_HREF_PREFIX}(\\d+(?:${CONTRACT_CITATION_HREF_INDEX_SEPARATOR}\\d+)*)$`,
	).exec(href);
	if (!match) return [];
	return match[1].split(CONTRACT_CITATION_HREF_INDEX_SEPARATOR).map((index) => parseInt(index, 10));
}

/**
 * Wraps each canonical `[n]` marker in a Markdown link carrying its reading-order index (`#cite-N`),
 * so the Markdown renderer turns it into a pill that resolves via `sources`/`citations` — a pure
 * lookup, no prose parsing. The answer is already canonical (server-normalised), so this only tags
 * markers. Markers with no backing source are left as plain text (defensive; the server should not
 * emit them, and pre-contract exchanges carry no sources so their `[n]` render as text).
 *
 * `markerIndex` must mirror the backend's `citations[]` ordering, which counts every `[n]` regardless
 * of context. So a marker inside a code block / Markdown link is still counted (index stays aligned)
 * but is NOT wrapped — we don't inject link syntax into code or break an existing link.
 */
function linkifyContractCitations(text: string, sources: SourceOut[]): string {
	const protectedRanges = getProtectedRanges(text);
	let markerIndex = -1;
	const linked = text.replace(/\[(\d+)]/g, (token, ordinal: string, offset: number) => {
		markerIndex += 1;
		if (isWithinProtectedRange(offset, protectedRanges)) return token;
		const source = sources[parseInt(ordinal, 10) - 1];
		return source ? buildCitationLink(token, `${CONTRACT_CITATION_HREF_PREFIX}${markerIndex}`) : token;
	});

	return linked.replace(new RegExp(`${citationLinkPattern}(?:\\s*${citationLinkPattern})+`, 'g'), (match: string) => {
		const indices = [...match.matchAll(new RegExp(citationLinkPattern, 'g'))].map((result) => result[1]);
		const markers = [...match.matchAll(/\\\[(\d+)\\\]/g)].map((result) => `[${result[1]}]`);
		const uniqueMarkers = [...new Set(markers)];
		return buildCitationLink(
			uniqueMarkers.join(' '),
			`${CONTRACT_CITATION_HREF_PREFIX}${indices.join(CONTRACT_CITATION_HREF_INDEX_SEPARATOR)}`,
		);
	});
}

/** The href a citation marker is tagged with while the answer is still streaming. Resolves to a
 * neutral skeleton pill (no source data exists yet) that swaps to the real pill at completion. */
export const LOADING_CITATION_HREF = '#cite-loading';

// Page/doc noise inside a marker, mirroring the backend normaliser so the streaming skeleton appears
// at exactly the markers that will become real pills (canonical `[n]`, multi-ref, `[Doc n]`, and
// page-only `[Page n]` drift). Kept here, not shared with the BE, because the FE only needs to detect
// a marker, never resolve it — a skeleton has no content to be wrong about.
const MARKER_PAGE_NOISE_RE = /\b(?:pages?|pp|pg|p)\.?\s*\d+(?:\s*[-–]\s*\d+)?/gi;
const MARKER_DOC_NOISE_RE = /\b(?:docs?|documents?)\.?/gi;
const MARKER_HAS_PAGE_REF_RE = /\b(?:pages?|pp|pg|p)\.?\s*\d/i;
const MARKER_INDEX_ONLY_RE = /^[\s\d,;&]*$/;

/** A closed bracket is a citation marker if, once page/doc noise is removed, it is only source
 * numbers (with at least one), or it is purely a page reference (`[Page 2]`). Letters/dots/slashes
 * left over mean a Markdown link, filename or prose — not a citation. */
function isCitationMarker(inner: string): boolean {
	const stripped = inner.replace(MARKER_PAGE_NOISE_RE, ' ').replace(MARKER_DOC_NOISE_RE, ' ');
	if (/\d/.test(stripped) && MARKER_INDEX_ONLY_RE.test(stripped)) return true;
	return MARKER_HAS_PAGE_REF_RE.test(inner) && stripped.replace(/[\s,;&.]/g, '') === '';
}

/**
 * Streaming counterpart to {@link linkifyContractCitations}: while the answer is still arriving the
 * server hasn't normalised it, so there is no `sources`/`citations` contract to resolve. Each
 * *complete* citation-shaped marker is wrapped in a `#cite-loading` link that renders a neutral
 * skeleton pill; at `turn_completed` the contract path replaces it with the labelled pill. Partial
 * markers (no closing bracket yet) are left as text so a half-streamed `[1` never flashes a pill.
 * The original token is kept as the link text as a graceful fallback if the skeleton can't render.
 *
 * No reading-order index here (the skeleton href is constant), so unlike the contract path this can
 * safely hide protected spans entirely — a `[1]` inside a code block / link is never skeletoned.
 */
export function linkifyLoadingCitations(text: string): string {
	return withProtectedMarkdown(text, (unprotected) =>
		unprotected.replace(/\[[^\][\n]+]/g, (token) =>
			isCitationMarker(token.slice(1, -1)) ? buildCitationLink(token, LOADING_CITATION_HREF) : token,
		),
	);
}

/**
 * Response-text pipeline for the chat transcript. The server delivers a canonical answer (only
 * `[n]` markers, no "Sources:" prose), so the client just moves trailing citations after the full
 * stop, tags each marker (a skeleton pill while still streaming, the contract-resolved pill once
 * complete), then linkifies contact details.
 */
export function formatContractAnswer(text: string, sources: SourceOut[], isLoading: boolean): string {
	const punctuated = moveCitationsAfterPunctuation(text);
	const reordered = isLoading ? punctuated : moveCitationsToParagraphEnd(punctuated);
	const linked = isLoading ? linkifyLoadingCitations(reordered) : linkifyContractCitations(reordered, sources);
	return linkifyContactDetails(linked);
}

/**
 * The cited pages for a source, de-duplicated and sorted ascending so the page chips render in a
 * stable, readable order regardless of the order the backend aggregated them in.
 */
export function getSortedUniquePages(pages: number[]): number[] {
	return [...new Set(pages)].sort((a, b) => a - b);
}

/**
 * A stable React key for a source. Prefers the document id (unique per cited document), then the
 * filename, falling back to the ordinal so the key is never empty for source-only citations.
 */
export function getSourceKey(source: SourceOut): string {
	return source.document_id ?? source.source_file ?? String(source.ordinal);
}

/**
 * Numbered record citations are compact enough to show four in full. Once there are more, the
 * fourth slot becomes the overflow count, avoiding a no-op `+1` pill while keeping four total.
 */
export function getRecordCitationPillLayout<T>(items: T[]): { visibleItems: T[]; hiddenCount: number } {
	const maxPills = 4;
	if (items.length <= maxPills) return { visibleItems: items, hiddenCount: 0 };
	const visibleCount = maxPills - 1;
	return { visibleItems: items.slice(0, visibleCount), hiddenCount: items.length - visibleCount };
}

export interface CitationPreviewEntry {
	source: SourceOut;
	citation: CitationOut;
	document?: Document;
}

export interface CitationPreviewGroup {
	source: SourceOut;
	citation: CitationOut;
	document?: Document;
	pages: number[];
}

const getCitationPreviewEntryKey = (entry: CitationPreviewEntry): string => {
	const documentKey = entry.source.document_id ?? entry.source.source_file ?? String(entry.citation.ordinal);
	return `${documentKey}:${entry.citation.page ?? 'document'}`;
};

const getCitationGroupKey = (entry: CitationPreviewEntry): string =>
	entry.source.document_id ?? entry.source.source_file ?? String(entry.citation.ordinal);

export function getCitationPreviewEntries(
	citationIndices: number[],
	citations: CitationOut[],
	sources: SourceOut[],
	documents: Document[],
): CitationPreviewEntry[] {
	const seen = new Set<string>();

	return citationIndices
		.map((citationIndex): CitationPreviewEntry | null => {
			const citation = citations[citationIndex];
			const source = citation ? sources[citation.ordinal - 1] : undefined;
			const recordId = source?.document_id != null ? parseInt(source.document_id, 10) : null;
			const document =
				recordId != null && !Number.isNaN(recordId) ? findDocumentById(documents, recordId) : undefined;
			return citation && source ? { citation, source, document } : null;
		})
		.filter((entry): entry is CitationPreviewEntry => entry !== null)
		.filter((entry) => {
			const key = getCitationPreviewEntryKey(entry);
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		});
}

export function groupCitationPreviewEntries(entries: CitationPreviewEntry[]): CitationPreviewGroup[] {
	const groups = new Map<string, CitationPreviewGroup>();

	entries.forEach((entry) => {
		const key = getCitationGroupKey(entry);
		const existing = groups.get(key);
		const pages = entry.citation.page != null ? [entry.citation.page] : (entry.source.pages ?? []);

		if (existing) {
			existing.pages = getSortedUniquePages([...existing.pages, ...pages]);
			if (!existing.document && entry.document) existing.document = entry.document;
			return;
		}

		groups.set(key, {
			source: entry.source,
			citation: entry.citation,
			document: entry.document,
			pages: getSortedUniquePages(pages),
		});
	});

	return Array.from(groups.values());
}

export const getExtraDocumentCount = (entries: CitationPreviewEntry[]): number =>
	Math.max(groupCitationPreviewEntries(entries).length - 1, 0);
